import React, { useLayoutEffect, useMemo, useRef } from "react";
import { IssueBox } from "./IssueBox";
import { MonthBand } from "./MonthBand";
import { SundayMarker } from "./SundayMarker";
import { SpecialIssuesBand } from "./SpecialIssuesBand";
import { computeMergedEndDates, formatDateLabel, formatDateRange, } from "../../utils/issueDateFormat";
import { computeMonthlyStats } from "../../utils/issueMonthStats";
import { computeGapMarkers } from "../../utils/issueGapMarkers";
import { placeIssues } from "../../utils/issueLayoutPlacement";
import { isCzechHoliday } from "../../utils/czechHolidays";
import { parseIsoDate, toIsoDateString } from "../../utils/isoDate";
import { monthKeyOf } from "../../utils/volumeWindow";
import css from "./IssueLayoutTier3.module.css";
var BOX_SIZE = 44;
var SUNDAY_MARKER_SIZE = 14;
var HOLIDAY_CELL_SIZE = 44;
var FLOW_GAP = 4;
function countInnerMarkers(startIso, endIso) {
    var _a = startIso.split("-").map(Number), sy = _a[0], sm = _a[1], sd = _a[2];
    var _b = endIso.split("-").map(Number), ey = _b[0], em = _b[1], ed = _b[2];
    var start = new Date(sy, sm - 1, sd);
    var end = new Date(ey, em - 1, ed);
    var sundays = 0;
    var holidays = 0;
    var cursor = new Date(start);
    cursor.setDate(cursor.getDate() + 1);
    while (cursor.getTime() < end.getTime()) {
        if (cursor.getDay() === 0)
            sundays += 1;
        else if (isCzechHoliday(cursor))
            holidays += 1;
        cursor.setDate(cursor.getDate() + 1);
    }
    return { sundays: sundays, holidays: holidays };
}
function mergedIssueWidth(mergedCount, sundays, holidays) {
    var slots = mergedCount + sundays + holidays;
    return (mergedCount * BOX_SIZE +
        sundays * SUNDAY_MARKER_SIZE +
        holidays * HOLIDAY_CELL_SIZE +
        (slots - 1) * FLOW_GAP);
}
var FlowRow = function (_a) {
    var children = _a.children;
    var ref = useRef(null);
    useLayoutEffect(function () {
        var flow = ref.current;
        if (!flow)
            return undefined;
        var apply = function () {
            var markers = Array.from(flow.querySelectorAll('[data-testid="sunday-marker"]'));
            markers.forEach(function (m) {
                m.style.display = "";
            });
            for (var pass = 0; pass < 8; pass++) {
                var hidden = false;
                for (var _i = 0, markers_1 = markers; _i < markers_1.length; _i++) {
                    var m = markers_1[_i];
                    if (m.style.display !== "none") {
                        var prev = m.previousElementSibling;
                        while (prev && prev.offsetParent === null) {
                            prev = prev.previousElementSibling;
                        }
                        if (!prev || m.offsetTop > prev.offsetTop) {
                            m.style.display = "none";
                            hidden = true;
                        }
                    }
                }
                if (!hidden)
                    break;
            }
        };
        apply();
        var ro = new ResizeObserver(apply);
        ro.observe(flow);
        return function () { return ro.disconnect(); };
    });
    return (React.createElement("div", { ref: ref, className: css.flow }, children));
};
export var IssueLayoutTier3 = function (_a) {
    var issues = _a.issues, onIssueClick = _a.onIssueClick, onLinkedVolumeClick = _a.onLinkedVolumeClick;
    var stats = useMemo(function () { return computeMonthlyStats(issues); }, [issues]);
    var markers = useMemo(function () { return computeGapMarkers(issues); }, [issues]);
    var mergedEndDates = useMemo(function () { return computeMergedEndDates(issues); }, [issues]);
    var placement = useMemo(function () { return placeIssues(issues); }, [issues]);
    var byMonth = useMemo(function () {
        var items = [];
        placement.dated.forEach(function (iss) {
            items.push({
                date: parseIsoDate(iss.expectedDate),
                kind: "ISSUE",
                issue: iss,
            });
        });
        markers.forEach(function (m) {
            items.push({ date: m.date, kind: "MARKER", marker: m });
        });
        items.sort(function (a, b) { return a.date.getTime() - b.date.getTime(); });
        var map = new Map();
        items.forEach(function (r) {
            var key = monthKeyOf(toIsoDateString(r.date)).key;
            if (!map.has(key))
                map.set(key, []);
            map.get(key).push(r);
        });
        return Array.from(map.entries()).map(function (_a) {
            var monthKey = _a[0], monthItems = _a[1];
            var trimmed = monthItems;
            var isSundayMarker = function (r) { var _a; return r.kind === "MARKER" && ((_a = r.marker) === null || _a === void 0 ? void 0 : _a.kind) === "SUNDAY"; };
            if (trimmed.length > 0 && isSundayMarker(trimmed[0])) {
                trimmed = trimmed.slice(1);
            }
            if (trimmed.length > 0 && isSundayMarker(trimmed[trimmed.length - 1])) {
                trimmed = trimmed.slice(0, -1);
            }
            return [monthKey, trimmed];
        });
    }, [placement, markers]);
    return (React.createElement("div", null,
        byMonth.map(function (_a) {
            var _b, _c;
            var monthKey = _a[0], items = _a[1];
            var _d = monthKey.split("-").map(Number), year = _d[0], month = _d[1];
            var s = stats.get(monthKey);
            return (React.createElement(React.Fragment, { key: monthKey },
                React.createElement(MonthBand, { month: month, year: year, received: (_b = s === null || s === void 0 ? void 0 : s.received) !== null && _b !== void 0 ? _b : 0, expected: (_c = s === null || s === void 0 ? void 0 : s.expected) !== null && _c !== void 0 ? _c : 0 }),
                React.createElement(FlowRow, null, items.map(function (r, idx) {
                    var _a, _b, _c;
                    if (r.kind === "ISSUE" && r.issue) {
                        var endIso = mergedEndDates.get(r.issue.id);
                        var mergedCount = (_b = (_a = r.issue.mergedIssueNumbers) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 1;
                        var dateLabel = endIso
                            ? formatDateRange(r.issue.expectedDate, endIso, 3)
                            : formatDateLabel(r.issue.expectedDate, 3);
                        var overrideWidth = void 0;
                        if (mergedCount > 1 && r.issue.expectedDate && endIso) {
                            var _d = countInnerMarkers(r.issue.expectedDate, endIso), sundays = _d.sundays, holidays = _d.holidays;
                            overrideWidth = mergedIssueWidth(mergedCount, sundays, holidays);
                        }
                        var followers = (_c = placement.followersByParentId.get(r.issue.id)) !== null && _c !== void 0 ? _c : [];
                        return (React.createElement(React.Fragment, { key: r.issue.id },
                            React.createElement(IssueBox, { issue: r.issue, onClick: onIssueClick, onLinkedVolumeClick: onLinkedVolumeClick, dateLabel: dateLabel, overrideWidth: overrideWidth }),
                            followers.map(function (follower) { return (React.createElement(IssueBox, { key: follower.id, issue: follower, onClick: onIssueClick, onLinkedVolumeClick: onLinkedVolumeClick })); })));
                    }
                    if (r.kind === "MARKER" && r.marker) {
                        var key = "marker-".concat(monthKey, "-").concat(idx, "-").concat(r.date.getTime());
                        if (r.marker.kind === "SUNDAY") {
                            return (React.createElement(SundayMarker, { key: key, kind: "SUNDAY", date: r.marker.date }));
                        }
                        return (React.createElement(SundayMarker, { key: key, kind: "HOLIDAY", date: r.marker.date, code: r.marker.code }));
                    }
                    return null;
                }))));
        }),
        React.createElement(SpecialIssuesBand, { issues: placement.unplaced, onIssueClick: onIssueClick, onLinkedVolumeClick: onLinkedVolumeClick })));
};
